import { motion } from 'motion/react'
import type { ReactNode } from 'react'
import { usePressMotion } from '../../lib/press-motion'
import { cn } from '../../lib/utils'

export interface BottomNavTabProps {
  // Pre-rendered icon, e.g. `<House size={24} weight="fill" />`. A node so the block stays decoupled
  // from any icon library; it inherits the tab's text color (Phosphor/Lucide icons use currentColor).
  icon: ReactNode
  label: string
  active: boolean
  // Scrolled state: the tab collapses to a centered dot (icon + label fade away).
  collapsed: boolean
  // Router-agnostic navigation. The consumer supplies the navigable element (web: react-router
  // `<Link>`); it receives the tab's className, aria-label, and the motion-wrapped children, and must
  // render a focusable, navigable element around them.
  renderLink: (props: { className: string; 'aria-label': string; children: ReactNode }) => ReactNode
}

// A single bottom-nav tab: icon + label (expanded) collapsing to a dot (scrolled), with the beUI
// spring-squish on press. Color alone marks the active tab (brand-green vs muted).
export function BottomNavTab({ icon, label, active, collapsed, renderLink }: BottomNavTabProps) {
  const press = usePressMotion()
  return renderLink({
    className: 'relative flex-1 min-w-0 flex',
    'aria-label': label,
    children: (
      <>
        {/* The pressable visual — the icon + label group squishes; fills the tab so a tap anywhere
            triggers it. */}
        <motion.span
          {...press}
          className={cn(
            'flex flex-1 flex-col items-center justify-center transition-colors',
            active ? 'text-brand-green' : 'text-muted-foreground hover:text-foreground'
          )}>
          <span className={cn('transition duration-300 ease-spring', collapsed ? 'opacity-0 scale-[0.4]' : 'opacity-100 scale-100')}>
            {icon}
          </span>
          {/* Label collapses its height so the pill can shrink (kept in the a11y tree, not display:none). */}
          <span
            className={cn(
              'overflow-hidden text-2xs font-medium leading-snug text-nowrap transition-all duration-300 ease-spring',
              collapsed ? 'max-h-0 -mt-1 opacity-0' : 'max-h-[14px] opacity-100'
            )}>
            {label}
          </span>
        </motion.span>
        {/* Dot — the collapsed representation; active dot is brand green and slightly larger. */}
        <span
          aria-hidden="true"
          className={cn(
            'absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full transition duration-300 ease-spring',
            active ? 'bg-brand-green' : 'bg-muted-foreground',
            collapsed ? (active ? 'opacity-100 scale-[1.35]' : 'opacity-100 scale-100') : 'opacity-0 scale-[0.3]'
          )}
        />
      </>
    )
  })
}
